import type { GuideWalkthroughDef } from "../types";
import { explainFocusStep } from "./steps/shared";

/**
 * How to fill in a transport line, field by field: sender, recipient, the two
 * dates, quantity + weight, the custom fields (Trafic / Type marchandise), the
 * custom columns (Palettes / Rolls), a comment, then how to delete the line and
 * how to validate or cancel.
 *
 * Runs INSIDE an editable order — `requires` gates on `recap-focus-editable`
 * (PENDING / AI_PROPOSAL) so the standard per-line cells are unlocked and
 * present. Each cell target is a STATIC testid resolved by first match, i.e.
 * the first line's cell — the overlay measures and spotlights that one.
 *
 * The custom fields/columns (Trafic, Type marchandise, Palettes, Rolls) are
 * TENANT-CONFIGURABLE: a transport tenant may not define all of them, so those
 * steps are `optional` — the overlay skips a step whose target never renders
 * instead of stalling the tour on a drift error. The final Save/Cancel bar is
 * likewise `optional`: it only mounts once the buffer has staged edits.
 *
 * Every step is EXPLAIN (`explainFocusStep`, `advanceOn: "next"`): the cells
 * open popovers / dropdowns and the guide is non-blocking — the user operates
 * the control at their own pace and taps Suivant, so an open dropdown never
 * bleeds into the next step's highlight.
 */
export const createTransportLine: GuideWalkthroughDef = {
  id: "create-transport-line",
  titleKey: "guide.walkthroughs.createTransportLine.title",
  requires: "recap-focus-editable",
  requiresBodyKey: "guide.requiresEditableOrder",
  steps: [
    // Sender — open the list and pick one (or force a new custom sender).
    explainFocusStep(
      "transport-sender-cell",
      "guide.walkthroughs.createTransportLine.senderPickBody",
    ),
    // Recipient — open the list and pick a destination.
    explainFocusStep(
      "transport-recipient-cell",
      "guide.walkthroughs.createTransportLine.recipientBody",
    ),
    // Dates — departure + arrival, both set inside the same popover.
    explainFocusStep(
      "transport-dates-trigger",
      "guide.walkthroughs.createTransportLine.datesBody",
    ),
    // Quantity — its own numeric cell (a separate table column from weight).
    explainFocusStep(
      "flow-quantity-input",
      "guide.walkthroughs.createTransportLine.quantityBody",
    ),
    // Weight — its own numeric cell.
    explainFocusStep(
      "flow-weight-input",
      "guide.walkthroughs.createTransportLine.weightBody",
    ),
    // Trafic — a tenant-configurable custom FIELD; `optional` when undefined.
    // The per-field handle resolves in both responsive renderings (inline
    // column ≥1280px, collapsed popover <1280px).
    explainFocusStep(
      "flow-custom-field-Trafic",
      "guide.walkthroughs.createTransportLine.traficBody",
      { optional: true },
    ),
    // Type marchandise — the second tenant-configurable custom FIELD.
    explainFocusStep(
      "flow-custom-field-Type marchandise",
      "guide.walkthroughs.createTransportLine.typeMarchandiseBody",
      { optional: true },
    ),
    // Palettes — a tenant-configurable inline custom COLUMN; `optional`.
    explainFocusStep(
      "flow-custom-Palettes-input",
      "guide.walkthroughs.createTransportLine.palettesBody",
      { optional: true },
    ),
    // Rolls — a tenant-configurable inline custom COLUMN; `optional`.
    explainFocusStep(
      "flow-custom-Rolls-input",
      "guide.walkthroughs.createTransportLine.rollsBody",
      { optional: true },
    ),
    // Comment.
    explainFocusStep(
      "flow-comment-cell",
      "guide.walkthroughs.createTransportLine.commentBody",
    ),
    // Delete the line.
    explainFocusStep(
      "flow-actions-delete",
      "guide.walkthroughs.createTransportLine.deleteBody",
    ),
    // The lines-table edit chips — Valider (save staged line edits) / Annuler
    // (discard). `recap-modify-bar` mounts only once edits are staged, so this
    // step is `optional`: on an order with none the tour skips it and still
    // finishes with the success toast instead of a drift error.
    explainFocusStep(
      "recap-modify-bar",
      "guide.walkthroughs.createTransportLine.modifyBarBody",
      { optional: true },
    ),
  ],
};
